<script setup lang="ts">
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { storeToRefs } from 'pinia'

withDefaults(
  defineProps<{
    backgroundLuminance: 'dark' | 'light'
  }>(),
  {
    backgroundLuminance: 'dark',
  },
)

const { isFrozen } = storeToRefs(useSurfaceStore())
const whiteboardModeStore = useWhiteboardModeStore()
</script>

<template>
  <div :class="['flex', 'flex-row', 'gap-x-1.5', 'items-center']">
    <OzContainedButton
      :class="[
        {
          'bg-dark-ui-200 text-light-text-100': backgroundLuminance === 'dark' && whiteboardModeStore.isDrawMode,
          'bg-light-ui-200 text-dark-text-100': backgroundLuminance === 'light' && whiteboardModeStore.isDrawMode,
        },
      ]"
      :aria-label="__('Enter Draw mode')"
      :title="__('Enter Draw mode')"
      :text="__('Draw')"
      :size-preset="OzContainedButtonSizePreset.H24px"
      :color-scheme="
        !whiteboardModeStore.isDrawMode
          ? OzContainedButtonColorScheme.SecondaryClear
          : OzContainedButtonColorScheme.Bare
      "
      :dark-mode="backgroundLuminance === 'dark'"
      data-testid="drawModeButton"
      @click="whiteboardModeStore.enterDrawMode"
    >
      <template #rightIcon>
        <OzIcon
          v-if="isFrozen"
          name="freeze"
          :size="16"
          :class="[
            'ms-1',
            {
              'text-dark-text-200': backgroundLuminance === 'light',
              'text-light-text-200': backgroundLuminance === 'dark',
            },
          ]"
          data-testid="whiteboardFreezeIcon"
        />
      </template>
    </OzContainedButton>
    <template v-if="isAppUsing('sandboxCode')">
      <span
        :class="[
          'text-body-small',
          'select-none',
          {
            'text-dark-text-400': backgroundLuminance === 'light',
            'text-light-text-400': backgroundLuminance === 'dark',
          },
        ]"
        aria-hidden="true"
      >
        /
      </span>
      <OzContainedButton
        :class="[
          {
            'bg-dark-ui-200 text-light-text-100': backgroundLuminance === 'dark' && whiteboardModeStore.isCodeMode,
            'bg-light-ui-200 text-dark-text-100': backgroundLuminance === 'light' && whiteboardModeStore.isCodeMode,
          },
        ]"
        :aria-label="__('Enter Code mode')"
        :title="__('Enter Code mode')"
        :size-preset="OzContainedButtonSizePreset.H24px"
        :color-scheme="
          !whiteboardModeStore.isCodeMode
            ? OzContainedButtonColorScheme.SecondaryClear
            : OzContainedButtonColorScheme.Bare
        "
        :dark-mode="backgroundLuminance === 'dark'"
        @click="whiteboardModeStore.enterCodeMode"
        >{{ __('Code') }}
      </OzContainedButton>
    </template>
    <span
      :class="[
        'text-body-small',
        'select-none',
        {
          'text-dark-text-400': backgroundLuminance === 'light',
          'text-light-text-400': backgroundLuminance === 'dark',
        },
      ]"
      aria-hidden="true"
    >
      /
    </span>
    <OzContainedButton
      :class="[
        {
          'bg-dark-ui-200 text-light-text-100': backgroundLuminance === 'dark' && whiteboardModeStore.isPlayMode,
          'bg-light-ui-200 text-dark-text-100': backgroundLuminance === 'light' && whiteboardModeStore.isPlayMode,
        },
      ]"
      :aria-label="__('Enter Play mode')"
      :title="__('Enter Play mode')"
      :size-preset="OzContainedButtonSizePreset.H24px"
      :color-scheme="
        !whiteboardModeStore.isPlayMode
          ? OzContainedButtonColorScheme.SecondaryClear
          : OzContainedButtonColorScheme.Bare
      "
      :dark-mode="backgroundLuminance === 'dark'"
      data-testid="playModeButton"
      @click="whiteboardModeStore.enterPlayMode"
      >{{ __('Play') }}
    </OzContainedButton>
  </div>
</template>
